import React, { useState, useEffect } from "react";
import { Users, Percent, DollarSign, Edit, Save, X } from "lucide-react";
import { api } from "../../api/greenclub";
export default function BusinessIntelligence() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [tempDesconto, setTempDesconto] = useState({
    tipo: "percentual",
    valor: 0,
  });
  useEffect(() => {
    carregarUsuarios();
  }, []);
  const carregarUsuarios = async () => {
    try {
      const response = await api.getAdminUsers();
      if (response.success) {
        const data = response;
        // Filtrar apenas médicos e representantes
        const medicosRepresentantes =
          data.users?.filter(
            (u) => u.tipo === "medico" || u.tipo === "representante"
          ) || [];
        setUsuarios(medicosRepresentantes);
      }
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    } finally {
      setLoading(false);
    }
  };
  const iniciarEdicao = (usuario) => {
    setEditando(usuario.id);
    setTempDesconto({
      tipo: usuario.desconto_tipo || "percentual",
      valor: usuario.desconto_valor || 0,
    });
  };
  const cancelarEdicao = () => {
    setEditando(null);
    setTempDesconto({ tipo: "percentual", valor: 0 });
  };
  const salvarDesconto = async (usuarioId) => {
    try {
      const response = await api.updateAdminUser(usuarioId, {
        desconto_tipo: tempDesconto.tipo,
        desconto_valor: tempDesconto.valor,
      });

      if (response.success) {
        // Atualizar a lista local
        setUsuarios((prev) =>
          prev.map((u) =>
            u.id === usuarioId
              ? {
                  ...u,
                  desconto_tipo: tempDesconto.tipo,
                  desconto_valor: tempDesconto.valor,
                }
              : u
          )
        );
        setEditando(null);
      } else {
        alert(response.error || "Erro ao salvar desconto");
      }
    } catch (error) {
      console.error("Erro ao salvar desconto:", error);
      alert("Erro ao salvar desconto");
    }
  };
  const formatarDesconto = (usuario) => {
    if (!usuario.desconto_valor) return "Não configurado";
    if (usuario.desconto_tipo === "percentual") {
      return `${usuario.desconto_valor}%`;
    }
    return `R$ ${usuario.desconto_valor.toFixed(2)}`;
  };
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Inteligência do Negócio
        </h2>
        <p className="text-gray-600">
          Configure descontos personalizados para médicos e representantes
        </p>
      </div>

      {/* Lista de Usuários */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Médicos e Representantes
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {usuarios.map((usuario) => (
            <div key={usuario.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                {/* Informações do Usuário */}
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        usuario.tipo === "medico"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-purple-100 text-purple-600"
                      }`}
                    >
                      <Users className="h-5 w-5" />
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-gray-900">
                      {usuario.nome}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          usuario.tipo === "medico"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {usuario.tipo === "medico" ? "Médico" : "Representante"}
                      </span>
                      <span className="text-sm text-gray-500">
                        {usuario.email}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Configuração de Desconto */}
                <div className="flex items-center space-x-4">
                  {editando === usuario.id ? (
                    // Modo de Edição
                    <div className="flex items-center space-x-3">
                      <select
                        value={tempDesconto.tipo}
                        onChange={(e) =>
                          setTempDesconto((prev) => ({
                            ...prev,
                            tipo: e.target.value,
                          }))
                        }
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="percentual">Percentual (%)</option>
                        <option value="valor_fixo">Valor Fixo (R$)</option>
                      </select>

                      <div className="relative">
                        {tempDesconto.tipo === "percentual" ? (
                          <Percent className="absolute left-2 top-1.5 h-3 w-3 text-gray-400" />
                        ) : (
                          <DollarSign className="absolute left-2 top-1.5 h-3 w-3 text-gray-400" />
                        )}
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={
                            tempDesconto.tipo === "percentual"
                              ? "100"
                              : undefined
                          }
                          value={tempDesconto.valor}
                          onChange={(e) =>
                            setTempDesconto((prev) => ({
                              ...prev,
                              valor: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className="pl-6 pr-2 py-1 border border-gray-300 rounded text-sm w-20"
                          placeholder={
                            tempDesconto.tipo === "percentual" ? "10" : "50"
                          }
                        />
                      </div>

                      <button
                        onClick={() => salvarDesconto(usuario.id)}
                        className="p-1 text-green-600 hover:text-green-800"
                        title="Salvar"
                      >
                        <Save className="h-4 w-4" />
                      </button>

                      <button
                        onClick={cancelarEdicao}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Cancelar"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    // Modo de Visualização
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          Desconto: {formatarDesconto(usuario)}
                        </div>
                        {usuario.desconto_valor && (
                          <div className="text-xs text-gray-500">
                            {usuario.desconto_tipo === "percentual"
                              ? "Percentual"
                              : "Valor Fixo"}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => iniciarEdicao(usuario)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                        title="Editar desconto"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {usuarios.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Nenhum médico ou representante encontrado
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Cadastre médicos e representantes para configurar seus descontos.
            </p>
          </div>
        )}
      </div>

      {/* Instruções */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Como usar:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>
            • Clique no ícone de edição ao lado do nome para configurar o
            desconto
          </li>
          <li>
            • Escolha entre <strong>Percentual (%)</strong> ou{" "}
            <strong>Valor Fixo (R$)</strong>
          </li>
          <li>
            • O desconto será aplicado automaticamente nas vendas do
            médico/representante
          </li>
          <li>• Deixe em 0 ou vazio para não aplicar desconto</li>
        </ul>
      </div>
    </div>
  );
}
